/**
 * Identidad de una fila de precio de venta, y cómo casa lo que manda Business
 * Central con lo que ya tiene la caja.
 *
 * Es la tarea T1 del TPV en `contrato-sincronizacion-incremental-precios-2026-09-14.md`.
 *
 * Antes la lectura incremental buscaba la fila local por producto, tipo y
 * código de venta. Esa terna no identifica un precio: Business Central los
 * distingue por ocho campos, y en producción hay ternas con varias filas
 * vigentes (201 en El Salvador, 58 en Costa Rica y 51 en Honduras, medido el
 * 14/09/2026). Cada cambio de una de esas filas pisaba en local a otra distinta.
 *
 * Ahora la identidad es:
 *  - el `Id` de la fila (su SystemId en Business Central), cuando viene. No
 *    cambia aunque la fila se renombre, que es lo que pasa al cambiar la fecha
 *    de inicio;
 *  - si no viene, los ocho campos de la clave de Business Central.
 *
 * Una fila guardada antes de que Business Central mandara el `Id` se encuentra
 * por los ocho campos, y desde esa lectura queda con su `Id`.
 */

/**
 * El `Id` normalizado. Business Central lo manda con llaves y en mayúsculas
 * (`{8B2F...}`) y la API v2 lo da sin llaves y en minúsculas: se compara sin
 * llaves y en minúsculas.
 */
export const idDePrecio = (precio) =>
  String(precio?.Id ?? "")
    .replace(/[{}]/g, "")
    .trim()
    .toLowerCase();

/** Los ocho campos de la clave de «Sales Price», tal como los manda getcurrentsalesprices. */
export const claveDePrecio = (precio) =>
  [
    precio?.Item_No,
    precio?.Sales_Type,
    precio?.Sales_Code,
    precio?.Starting_Date,
    precio?.Currency_Code,
    precio?.Variant_Code,
    precio?.Unit_of_Measure_Code,
    precio?.Minimum_Quantity,
  ]
    .map((valor) => String(valor ?? "").trim())
    .join("|");

/**
 * El descuento de línea que trae la fila de precio, tal como lo manda
 * getcurrentsalesprices (hoy sólo Perú). El nombre lleva el `%` literal.
 */
export const CAMPO_DESCUENTO_DE_LINEA = "LineDiscount%";

/** El porcentaje de descuento de una fila de precio, en número. */
export const descuentoDelPrecio = (precio) => {
  const numero = Number(precio?.[CAMPO_DESCUENTO_DE_LINEA]);
  return Number.isFinite(numero) && numero > 0 ? numero : 0;
};

/**
 * La fila tal como se guarda en la base local: todo lo que manda Business
 * Central, más las marcas de sincronización.
 *
 * `Line_Discount_Pct` se guarda siempre, aunque Business Central no mande el
 * campo: así se distingue una fila guardada antes de que existiera, que hay que
 * releer, de una que simplemente no tiene descuento.
 */
export const aRegistroLocal = (precio, ahora = new Date()) => ({
  ...precio,
  Id: precio?.Id ?? "",
  Line_Discount_Pct: descuentoDelPrecio(precio),
  pendingSync: false,
  lastModified: ahora,
  lastSynced: ahora,
  deleted: false,
});

/**
 * Decide qué escribir en la base local con lo que devolvió una lectura
 * incremental.
 *
 * @param {Array} locales     Filas que ya tiene la caja, con su `id` de Dexie.
 * @param {Array} delServidor Filas que devolvió getcurrentsalesprices.
 * @param {Date}  [ahora]
 * @returns {{ registros: Array, actualizados: number, nuevos: number, conCambiosLocales: number }}
 *   `registros` va tal cual a `bulkPut`: los que casan llevan el `id` local y
 *   se actualizan; los demás no lo llevan y se agregan.
 */
export const emparejarPrecios = (locales, delServidor, ahora = new Date()) => {
  const porId = new Map();
  const porClave = new Map();

  for (const local of locales || []) {
    const id = idDePrecio(local);
    if (id && !porId.has(id)) porId.set(id, local);

    const clave = claveDePrecio(local);
    if (!porClave.has(clave)) porClave.set(clave, local);
  }

  // Dos filas del servidor no pueden caer sobre la misma fila local: la segunda
  // pisaría a la primera, que es justo el defecto que esto corrige.
  const usados = new Set();
  const registros = [];
  let actualizados = 0;
  let nuevos = 0;
  let conCambiosLocales = 0;

  for (const precio of delServidor || []) {
    const id = idDePrecio(precio);
    let local = id ? porId.get(id) : undefined;

    if (!local) {
      // Por los ocho campos, sólo si a alguno de los dos lados le falta el `Id`.
      // Si los dos lo tienen y es distinto, son dos precios distintos aunque hoy
      // coincida la clave: pasa cuando una fila se renombra y otra ocupa su clave.
      const porLaClave = porClave.get(claveDePrecio(precio));
      if (porLaClave && (!id || !idDePrecio(porLaClave))) local = porLaClave;
    }

    if (local && usados.has(local.id)) local = undefined;

    if (local) {
      usados.add(local.id);

      // Una fila con cambios locales sin enviar no se pisa con Business Central.
      if (local.pendingSync) {
        conCambiosLocales += 1;
        continue;
      }

      registros.push({ ...local, ...aRegistroLocal(precio, ahora), id: local.id });
      actualizados += 1;
    } else {
      registros.push(aRegistroLocal(precio, ahora));
      nuevos += 1;
    }
  }

  return { registros, actualizados, nuevos, conCambiosLocales };
};
